import { useRouter } from 'next/router'
import { type FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { isAddress } from 'viem'
import type { Address } from 'viem/accounts'

import BaseModal from 'components/BaseModal'
import Button from 'components/Button'
import ErrorView from 'components/ErrorView'
import Input from 'components/Input/Input'
import InputHeaderRow from 'components/Input/InputHeaderRow'
import { Body as SubTitle } from 'components/Text'
import { FlexDivCol } from 'components/layout/flex'
import ROUTES from 'constants/routes'
import useENS from 'hooks/useENS'
import { setOpenModal } from 'state/app/reducer'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { resetWalletAddress } from 'state/wallet/actions'

import { WatcherModeIcon } from './WatchOrConnectWallet'

const WatcherModeModal: FC = () => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const router = useRouter()
	const [watchWalletAddressInput, setWatchWalletAddressInput] = useState<string>('')
	const { ensAddress } = useENS(watchWalletAddressInput)
	const perpsProvider = useAppSelector(selectPerpsProvider)

	const watchWalletAddress = useMemo(() => {
		if (ensAddress) {
			return ensAddress
		}

		if (watchWalletAddressInput && isAddress(watchWalletAddressInput)) {
			return watchWalletAddressInput
		}

		return null
	}, [watchWalletAddressInput, ensAddress])

	const handleWatchWalletAddress = useCallback(() => {
		if (watchWalletAddress && isAddress(watchWalletAddress)) {
			dispatch(
				resetWalletAddress({ address: watchWalletAddress as Address, selectedType: 'watcher' })
			)
			router.push(ROUTES.Markets.Watcher(watchWalletAddress, perpsProvider))
			dispatch(setOpenModal(null))
		}
	}, [watchWalletAddress, dispatch, router, perpsProvider])

	return (
		<StyledBaseModal
			title={t('futures.market.trade.margin.modal.watcher.title')}
			isOpen
			onDismiss={() => dispatch(setOpenModal(null))}
		>
			<ModalBody rowGap="25px">
				<SubTitle color="secondary">
					{t('futures.market.trade.margin.modal.watcher.subtitle')}:
				</SubTitle>
				<FlexDivCol>
					<InputHeaderRow label="Address or ENS" />
					<WatcherInput
						autoFocus
						placeholder="0x"
						onChange={(e) => setWatchWalletAddressInput(e.target.value)}
					/>
				</FlexDivCol>
				<ErrorView
					containerStyle={{ padding: '10px', margin: '0px' }}
					message={t('futures.market.trade.margin.modal.watcher.exit-instructions')}
					messageType="warn"
				/>
				<Button
					disabled={!watchWalletAddress}
					size="small"
					variant="flat"
					noOutline
					onClick={handleWatchWalletAddress}
					mono
				>
					<WatcherModeIcon disabled={!watchWalletAddress} />
					{t('common.wallet.watch')}
				</Button>
			</ModalBody>
		</StyledBaseModal>
	)
}

const StyledBaseModal = styled(BaseModal)`
	[data-reach-dialog-content] {
		width: 448px;
	}
	.card-header {
		margin-top: 15px;
	}
`

const WatcherInput = styled(Input)`
	height: 40px;
	font-size: 13px;
	font-weight: 400;
	font-feature-settings: 'tnum';
`

const ModalBody = styled(FlexDivCol)`
	padding-top: 17px;
`

export default WatcherModeModal
